import type {
    IMessageBus,
    FabricTopicToPayload,
    MetaData,
    Source
} from '@casumo/fabric-fundamentals';

export type Topics = keyof FabricTopicToPayload;

type Subscription<Topic extends Topics> = {
    onMessage: (message: { payload: FabricTopicToPayload[Topic]; metaData: MetaData }) => void;
};

class MessageBus {
    private subscriptions: Map<Topics, Set<Subscription<Topics>>> = new Map();

    publish<Topic extends Topics>(
        topic: Topic,
        payload: FabricTopicToPayload[Topic],
        source: Source,
        metaData = { source }
    ) {
        metaData.source ||= source;
        this.subscriptions
            .get(topic)
            ?.forEach((subscription) => subscription.onMessage({ payload, metaData }));
    }

    subscribe<Topic extends Topics>(
        topic: Topic,
        handler: (payload: FabricTopicToPayload[Topic], metaData: MetaData) => void
    ) {
        const onMessage = (message: { payload: FabricTopicToPayload[Topic]; metaData: MetaData }) =>
            handler(message.payload, message.metaData);
        const subscription = { onMessage } as Subscription<Topics>;
        const subscriptions = this.subscriptions.get(topic);
        if (!subscriptions) {
            this.subscriptions.set(topic, new Set([subscription]));
        } else {
            subscriptions.add(subscription);
        }
        const unsubscribe = () => {
            this.subscriptions.get(topic)?.delete(subscription);
        };
        unsubscribe.unsubscribe = unsubscribe;
        return unsubscribe;
    }
}
class SourcedMessageBus implements IMessageBus<FabricTopicToPayload> {
    constructor(
        private messageBus: MessageBus,
        private source: Source
    ) {}
    publish<Topic extends Topics>(
        topic: Topic,
        payload: FabricTopicToPayload[Topic],
        metaData?: MetaData
    ) {
        this.messageBus.publish(topic, payload, this.source, metaData);
    }
    subscribe<Topic extends Topics>(
        topic: Topic,
        handler: (payload: FabricTopicToPayload[Topic], metaData: MetaData) => void
    ) {
        return this.messageBus.subscribe(topic, handler);
    }
}

const messageBus = new MessageBus();

export const sourcedBus = (source: string): IMessageBus<FabricTopicToPayload> =>
    new SourcedMessageBus(messageBus, source);

export const spineBus = sourcedBus('spine');
